import { useState, useRef, FC, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

import MDButton from "components/MDButton";
import { Checkbox } from "@mui/material";
import File from "assets/Icons/file.svg?react";
import ArrowLeftCircle from "assets/Icons/arrowLeftCircle.svg?react";
import FileCircle from "assets/Icons/fileCircle.svg?react";

import classNames from "classnames";
import { useMaterialUIController } from "context";
import { makeStyles } from "tss-react/mui";
import { CameraDeniedModal } from "components/CameraDeniedModal/CameraDeniedModal";
import { checkCameraPermission } from "utils/camera.util";
import { styles } from "./TermsAndConditions.styles";
import { HMO } from "constants/hmo";
import { detectHmo } from "utils/hmoDetection";

interface StepProps {
  handleUpdateStep: (step: number) => void;
}

const isLeumit = detectHmo() === HMO.LEUMIT;
const TermsAndConditionsIcon = isLeumit ? FileCircle : File;

const TermsAndConditions: FC<StepProps> = ({ handleUpdateStep }) => {
  const [permisionDeniedVissible, setPermisionDeniedVissible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [{ direction }] = useMaterialUIController();
  const [enableNext, setEnableNext] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleCheckTerms = (event: ChangeEvent<HTMLInputElement>) => {
    setEnableNext(event.target.checked);
  };

  const handleToggleExpand = () => {
    setIsExpanded((prevExpanded) => {
      const nextExpanded = !prevExpanded;

      if (!nextExpanded && contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      return nextExpanded;
    });
  };

  const goNextStep = async () => {
    try {
      await checkCameraPermission();
      setPermisionDeniedVissible(false);
      handleUpdateStep(1);
    } catch {
      setPermisionDeniedVissible(true);
    }
  };

  if (permisionDeniedVissible) {
    return (<CameraDeniedModal tryAgain={() => handleUpdateStep(1)}/>)
  }

  return (
    <div className={classes.container} data-test-id="terms-and-conditions">
      <div className={classes.bodyContainer}>
        <div
          className={classNames(
            classes.bodyContent
          )}
        >
          <div
            className={classNames(
              classes.itemCard,
              isExpanded && classes.itemCardExpanded
            )}
            data-test-id="terms-and-conditions-item"
          >
            <button
              type="button"
              className={classes.header}
              onClick={handleToggleExpand}
              aria-expanded={isExpanded}
              aria-controls="terms-and-conditions-content"
              data-test-id="terms-and-conditions-header"
            >
              <div className={classes.fileRow}>
                <TermsAndConditionsIcon />
                <div
                  className={classNames(
                    classes.detailsContainer,
                    direction === "ltr"
                      ? classes.detailsContainerLTR
                      : classes.detailsContainerRTL
                  )}
                >
                  <div className={classes.detailsTitle}>
                    {t("common:TermsAndConditions:title")}
                  </div>
                  <div className={classes.date}>28/06/2023</div>
                </div>
              </div>
              <span
                className={classNames(
                  classes.toggleIcon,
                  direction === "ltr"
                    ? classes.toggleIconLTR
                    : classes.toggleIconRTL,
                  isExpanded && classes.toggleIconExpanded
                )}
                data-test-id="terms-and-conditions-toggle"
                aria-hidden
              >
                <ArrowLeftCircle />
              </span>
            </button>
            <div
              id="terms-and-conditions-content"
              ref={contentRef}
              className={classNames(
                classes.content,
                isExpanded ? classes.contentExpanded : classes.contentCollapsed
              )}
              data-test-id="terms-and-conditions-content"
            >
              {t("common:TermsAndConditions:description")}
            </div>
          </div>
        </div>
        <div className={classes.approveCheckBoxContainer}>
          <Checkbox
            color="primary"
            classes={{
              root:
                direction === "ltr" ? classes.checkBoxLTR : classes.checkBoxRTL,
            }}
            onChange={handleCheckTerms}
            data-test-id="terms-and-conditions-checkbox"
            id="terms-and-conditions-checkbox"
          />
          <label htmlFor="terms-and-conditions-checkbox">
            {t("common:TermsAndConditions:checkBox:label")}
          </label>
        </div>
      </div>
      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={goNextStep}
        disabled={!enableNext}
        data-test-id="terms-and-conditions-next"
      >
        {t("common:nextScreen")}
      </MDButton>
    </div>
  );
};

export default TermsAndConditions;
